import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PlatformAnnouncement } from '@/types/platformContent';

export type AnnouncementEvent =
  | 'shown'
  | 'viewed'
  | 'dismissed'
  | 'postponed'
  | 'acknowledged'
  | 'cta_clicked'
  | 'never_show_again';

const LOCAL_KEY = 'autofluxo:announcements:seen';

/** Otimização complementar — a fonte de verdade continua sendo o banco. */
const readLocalSeen = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
  } catch {
    return {};
  }
};

export const markLocalSeen = (id: string) => {
  try {
    const seen = readLocalSeen();
    seen[id] = Date.now();
    localStorage.setItem(LOCAL_KEY, JSON.stringify(seen));
  } catch {
    /* ignore */
  }
};

/**
 * Busca os comunicados elegíveis para o usuário logado.
 * Nunca bloqueia o boot: é uma consulta secundária, com atraso e sem retry agressivo.
 */
export const useEligibleAnnouncements = (enabled: boolean, userId?: string) => {
  return useQuery<PlatformAnnouncement[]>({
    queryKey: ['announcements', 'eligible', userId || 'anon'],
    enabled,
    retry: 1,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_eligible_announcements');
      if (error) throw error;
      return (data || []) as PlatformAnnouncement[];
    },
  });
};

export const useRecordAnnouncementEvent = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, event }: { id: string; event: AnnouncementEvent }) => {
      const { error } = await supabase.rpc('record_announcement_event', {
        _announcement_id: id,
        _event: event,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      if (variables.event !== 'shown') {
        queryClient.invalidateQueries({ queryKey: ['announcements', 'eligible'] });
      }
    },
  });

  const record = useCallback(
    (id: string, event: AnnouncementEvent) => {
      if (event === 'shown') markLocalSeen(id);
      mutation.mutate({ id, event });
    },
    [mutation],
  );

  return { record };
};
